import { useState, useEffect, useMemo } from 'react';
import { 
    Clock, 
    Loader2, 
    Activity, 
    Search, 
    Download, 
    Calendar, 
    Filter, 
    Users, 
    RefreshCw, 
    CheckCircle2, 
    FileSpreadsheet,
    Layers,
    Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function ActivityLogsDetailed() {
    const [activities, setActivities] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedAction, setSelectedAction] = useState('all');
    const [dateRange, setDateRange] = useState<'all' | 'today' | '7days' | '30days'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchLogs = async () => {
        setLoading(true);
        try {
            // Fetch all logs for thorough audit tracking
            const response = await fetch(`${API_URL}/instructor/activity-logs?all=1`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setActivities(Array.isArray(data) ? data : []);
            } else {
                toast.error('Failed to load activity logs.');
            }
        } catch (err) {
            console.error("Failed to fetch activity logs:", err);
            toast.error('Network error loading activity logs.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [API_URL]);

    // Extract unique actions for filter dropdown
    const availableActions = useMemo(() => {
        const set = new Set<string>();
        activities.forEach(a => {
            if (a.action) set.add(a.action);
        });
        return Array.from(set).sort();
    }, [activities]);

    // Filter logs based on search, action, and dateRange
    const filteredActivities = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        return activities.filter(activity => {
            // Action filter
            if (selectedAction !== 'all' && activity.action !== selectedAction) {
                return false;
            }

            // Date filter
            if (dateRange !== 'all' && activity.created_at) {
                const itemDate = new Date(activity.created_at);
                if (dateRange === 'today' && itemDate < startOfToday) return false;
                if (dateRange === '7days' && itemDate < sevenDaysAgo) return false;
                if (dateRange === '30days' && itemDate < thirtyDaysAgo) return false;
            }

            // Search query
            if (!search.trim()) return true;
            const q = search.toLowerCase();

            const matchDesc = activity.description?.toLowerCase().includes(q);
            const matchUser = activity.user?.name?.toLowerCase().includes(q) || activity.user?.email?.toLowerCase().includes(q);
            const matchAction = activity.action?.toLowerCase().includes(q);
            const matchInstructor = activity.instructor?.name?.toLowerCase().includes(q);
            
            let matchMeta = false;
            if (activity.metadata) {
                const metaStr = typeof activity.metadata === 'string' ? activity.metadata : JSON.stringify(activity.metadata);
                matchMeta = metaStr.toLowerCase().includes(q);
            }

            return matchDesc || matchUser || matchAction || matchInstructor || matchMeta;
        });
    }, [activities, search, selectedAction, dateRange]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, selectedAction, dateRange, itemsPerPage]);

    // KPI Metrics
    const metrics = useMemo(() => {
        const total = activities.length;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayCount = activities.filter(a => new Date(a.created_at) >= startOfToday).length;
        
        const userSet = new Set();
        activities.forEach(a => {
            if (a.user_id) userSet.add(a.user_id);
            else if (a.user?.email) userSet.add(a.user.email);
        });

        return {
            total,
            todayCount,
            uniqueUsers: userSet.size,
            filteredCount: filteredActivities.length
        };
    }, [activities, filteredActivities]);

    // CSV Exporter
    const exportClientCSV = (logs: any[], exportAll: boolean) => {
        const headers = [
            'Log ID',
            'User Name',
            'User Email',
            'User Role',
            'Action Type',
            'Description',
            'Metadata Details',
            'Logged By / Instructor',
            'Timestamp (UTC)',
            'Timestamp (Formatted)'
        ];

        const rows = logs.map(log => {
            let metadataStr = '';
            if (log.metadata) {
                const meta = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                if (meta && typeof meta === 'object') {
                    metadataStr = Object.entries(meta).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' | ');
                }
            }

            const cleanField = (val: any) => {
                if (val === null || val === undefined) return '""';
                const str = String(val).replace(/"/g, '""');
                return `"${str}"`;
            };

            const createdAt = log.created_at ? new Date(log.created_at) : null;

            return [
                cleanField(log.id),
                cleanField(log.user?.name || 'System / Unassigned'),
                cleanField(log.user?.email || 'N/A'),
                cleanField(log.user?.role || 'system'),
                cleanField(log.action),
                cleanField(log.description),
                cleanField(metadataStr),
                cleanField(log.instructor?.name || 'System'),
                cleanField(createdAt ? createdAt.toISOString() : ''),
                cleanField(createdAt ? createdAt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'medium' }) : '')
            ].join(',');
        });

        // Add UTF-8 BOM
        const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        a.download = `layos_activity_logs_${exportAll ? 'all' : 'filtered'}_${timestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
    };

    const handleExportCSV = async (exportAll: boolean = false) => {
        setExporting(true);
        const toastId = toast.loading('Generating Activity Logs CSV export...');

        try {
            // First attempt server-side stream export
            const exportUrl = `${API_URL}/instructor/activity-logs/export${exportAll ? '' : `?action=${selectedAction}&search=${encodeURIComponent(search)}`}`;
            const response = await fetch(exportUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                a.download = `layos_activity_logs_${exportAll ? 'all' : 'filtered'}_${timestamp}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(downloadUrl);

                toast.success(`Successfully exported ${exportAll ? activities.length : filteredActivities.length} logs!`, { id: toastId });
            } else {
                // Fallback: Generate directly in client from loaded state
                const dataToExport = exportAll ? activities : filteredActivities;
                exportClientCSV(dataToExport, exportAll);
                toast.success(`Exported ${dataToExport.length} logs via browser export!`, { id: toastId });
            }
        } catch (err) {
            console.warn("Backend export stream fallback to client export:", err);
            const dataToExport = exportAll ? activities : filteredActivities;
            exportClientCSV(dataToExport, exportAll);
            toast.success(`Exported ${dataToExport.length} activity logs!`, { id: toastId });
        } finally {
            setExporting(false);
        }
    };

    const totalPages = Math.ceil(filteredActivities.length / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedActivities = filteredActivities.slice(startIndex, startIndex + itemsPerPage);

    if (loading && activities.length === 0) {
        return (
            <div style={{ height: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
                <Loader2 className="animate-spin" size={40} color="var(--index-primary-color)" />
                <p style={{ fontWeight: 800, color: 'var(--index-text-secondary)' }}>Loading All Activity Logs...</p>
            </div>
        );
    }

    return (
        <div className="animate-fade-in-up" style={{ padding: '0 1rem' }}>
            {/* Header with Export Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1.25rem' }}>
                <div>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 950, color: 'var(--index-text-heading)', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Activity size={28} color="var(--index-primary-color)" /> Activity Logs & Audit Trail
                    </h2>
                    <p style={{ color: 'var(--index-text-secondary)', margin: 0, fontWeight: 600, fontSize: '1.05rem' }}>
                        Detailed historical tracking of student enrollment, payments, submissions, and system events.
                    </p>
                </div>

                {/* Export Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={fetchLogs}
                        disabled={loading}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '0.65rem 1rem',
                            borderRadius: '12px',
                            border: '1.5px solid var(--index-border-color)',
                            background: 'var(--index-card-bg)',
                            color: 'var(--index-text-secondary)',
                            fontWeight: 800,
                            fontSize: '0.85rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s'
                        }}
                        title="Refresh Logs"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                        <span>Refresh</span>
                    </button>

                    <button
                        onClick={() => handleExportCSV(true)}
                        disabled={exporting || activities.length === 0}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '0.65rem 1.25rem',
                            borderRadius: '12px',
                            border: 'none',
                            background: 'linear-gradient(135deg, var(--index-primary-color) 0%, #0d9488 100%)',
                            color: '#ffffff',
                            fontWeight: 900,
                            fontSize: '0.88rem',
                            cursor: (exporting || activities.length === 0) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 14px color-mix(in srgb, var(--index-primary-color) 30%, transparent)',
                            transition: 'all 0.2s'
                        }}
                    >
                        {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        <span>Export All Logs ({activities.length})</span>
                    </button>
                </div>
            </div>

            {/* Quick Metrics Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: '1rem',
                marginBottom: '1.75rem'
            }}>
                <div style={{ background: 'var(--index-card-bg)', borderRadius: '18px', padding: '1.25rem 1.5rem', border: '1px solid var(--index-border-subtle)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'color-mix(in srgb, var(--index-primary-color) 12%, transparent)', color: 'var(--index-primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Activity size={22} />
                    </div>
                    <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Activities</span>
                        <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>{metrics.total.toLocaleString()}</h3>
                    </div>
                </div>

                <div style={{ background: 'var(--index-card-bg)', borderRadius: '18px', padding: '1.25rem 1.5rem', border: '1px solid var(--index-border-subtle)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'color-mix(in srgb, #006aff 12%, transparent)', color: '#006aff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={22} />
                    </div>
                    <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Users Tracked</span>
                        <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>{metrics.uniqueUsers.toLocaleString()}</h3>
                    </div>
                </div>

                <div style={{ background: 'var(--index-card-bg)', borderRadius: '18px', padding: '1.25rem 1.5rem', border: '1px solid var(--index-border-subtle)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'color-mix(in srgb, #10b981 12%, transparent)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Clock size={22} />
                    </div>
                    <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Activities Today</span>
                        <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>{metrics.todayCount.toLocaleString()}</h3>
                    </div>
                </div>

                <div style={{ background: 'var(--index-card-bg)', borderRadius: '18px', padding: '1.25rem 1.5rem', border: '1px solid var(--index-border-subtle)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'color-mix(in srgb, #ca8a04 12%, transparent)', color: '#ca8a04', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileSpreadsheet size={22} />
                    </div>
                    <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Filter Results</span>
                        <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>{metrics.filteredCount.toLocaleString()}</h3>
                    </div>
                </div>
            </div>

            {/* Filter & Search Bar Card */}
            <div style={{ background: 'var(--index-card-bg)', borderRadius: '24px', border: '1px solid color-mix(in srgb, var(--index-border-color) 80%, transparent)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                <div style={{
                    padding: '1.25rem 1.75rem',
                    borderBottom: '1px solid var(--index-border-subtle)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    background: 'var(--index-hover-bg)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', flex: '1 1 500px' }}>
                        {/* Search Input */}
                        <div style={{ position: 'relative', minWidth: '240px', flex: '1 1 240px' }}>
                            <Search size={18} color="var(--index-text-faint)" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input
                                type="text"
                                placeholder="Search by student, email, action, description..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px 10px 38px',
                                    borderRadius: '10px',
                                    border: '1.5px solid var(--index-border-color)',
                                    fontSize: '0.88rem',
                                    fontWeight: 600,
                                    color: 'var(--index-text-heading)',
                                    outline: 'none',
                                    background: 'var(--index-card-bg)',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Action Type Dropdown */}
                        <div style={{ minWidth: '170px' }}>
                            <select
                                value={selectedAction}
                                onChange={(e) => setSelectedAction(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    borderRadius: '10px',
                                    border: '1.5px solid var(--index-border-color)',
                                    fontSize: '0.86rem',
                                    fontWeight: 700,
                                    color: 'var(--index-text-heading)',
                                    background: 'var(--index-card-bg)',
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="all">All Action Types</option>
                                {availableActions.map(act => (
                                    <option key={act} value={act}>
                                        {act.replace(/_/g, ' ').toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range Selector */}
                        <div style={{ display: 'flex', gap: '4px', background: 'var(--index-card-bg)', padding: '3px', borderRadius: '10px', border: '1.5px solid var(--index-border-color)' }}>
                            {(['all', 'today', '7days', '30days'] as const).map(d => (
                                <button
                                    key={d}
                                    onClick={() => setDateRange(d)}
                                    style={{
                                        padding: '5px 10px',
                                        borderRadius: '7px',
                                        border: 'none',
                                        fontSize: '0.78rem',
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        background: dateRange === d ? 'var(--index-primary-color)' : 'transparent',
                                        color: dateRange === d ? '#ffffff' : 'var(--index-text-secondary)',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    {d === 'all' ? 'All' : d === 'today' ? 'Today' : d === '7days' ? '7 Days' : '30 Days'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Filter Action Export */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {filteredActivities.length !== activities.length && (
                            <button
                                onClick={() => handleExportCSV(false)}
                                disabled={exporting || filteredActivities.length === 0}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '0.55rem 0.95rem',
                                    borderRadius: '10px',
                                    border: '1.5px solid var(--index-border-color)',
                                    background: 'var(--index-card-bg)',
                                    color: 'var(--index-text-heading)',
                                    fontWeight: 800,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                }}
                                title="Export filtered records only"
                            >
                                <Download size={14} /> Export Filtered ({filteredActivities.length})
                            </button>
                        )}

                        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-secondary)', background: 'var(--index-border-color)', padding: '6px 14px', borderRadius: '100px' }}>
                            {filteredActivities.length} Records
                        </span>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: 'var(--index-card-bg)', borderBottom: '2px solid var(--index-border-subtle)' }}>
                                <th style={{ padding: '1.1rem 1.75rem', fontSize: '0.82rem', fontWeight: 800, color: 'var(--index-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
                                <th style={{ padding: '1.1rem 1.75rem', fontSize: '0.82rem', fontWeight: 800, color: 'var(--index-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action Type</th>
                                <th style={{ padding: '1.1rem 1.75rem', fontSize: '0.82rem', fontWeight: 800, color: 'var(--index-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description & Metadata</th>
                                <th style={{ padding: '1.1rem 1.75rem', fontSize: '0.82rem', fontWeight: 800, color: 'var(--index-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timestamp</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedActivities.length > 0 ? paginatedActivities.map((activity, idx) => {
                                const actionColor = 
                                    activity.action === 'enrolled' ? '#10b981' :
                                    activity.action === 'certificate_issued' ? '#ca8a04' :
                                    activity.action === 'payment' ? '#006aff' :
                                    activity.action === 'submission' ? '#8b5cf6' :
                                    'var(--index-primary-color)';

                                return (
                                    <tr 
                                        key={activity.id || idx} 
                                        style={{ 
                                            borderBottom: '1px solid var(--index-border-subtle)', 
                                            transition: 'background 0.2s', 
                                            background: idx % 2 === 0 ? 'var(--index-card-bg)' : 'var(--index-hover-bg)' 
                                        }} 
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--index-hover-bg)'} 
                                        onMouseOut={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'var(--index-card-bg)' : 'var(--index-hover-bg)'}
                                    >
                                        <td style={{ padding: '1.1rem 1.75rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ 
                                                    width: '38px', 
                                                    height: '38px', 
                                                    borderRadius: '10px', 
                                                    background: `color-mix(in srgb, ${actionColor} 12%, transparent)`, 
                                                    color: actionColor, 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    fontWeight: 900, 
                                                    fontSize: '0.92rem' 
                                                }}>
                                                    {activity.user?.name ? activity.user.name.charAt(0).toUpperCase() : <Shield size={18} />}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 850, color: 'var(--index-text-heading)', fontSize: '0.92rem' }}>
                                                        {activity.user?.name || 'System / Unassigned'}
                                                    </div>
                                                    {activity.user?.email && (
                                                        <span style={{ fontSize: '0.78rem', color: 'var(--index-text-faint)', fontWeight: 600, display: 'block' }}>
                                                            {activity.user.email}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.1rem 1.75rem' }}>
                                            <span style={{ 
                                                padding: '5px 10px', 
                                                background: `color-mix(in srgb, ${actionColor} 12%, transparent)`, 
                                                color: actionColor, 
                                                borderRadius: '8px', 
                                                fontSize: '0.76rem', 
                                                fontWeight: 850, 
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {activity.action ? activity.action.replace(/_/g, ' ') : 'LOG'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1.1rem 1.75rem', maxWidth: '480px' }}>
                                            <div style={{ fontWeight: 600, color: 'var(--index-text-secondary)', fontSize: '0.92rem', lineHeight: 1.45 }}>
                                                {activity.description}
                                            </div>
                                            {(() => {
                                                if (!activity.metadata) return null;
                                                let parsed = {};
                                                try {
                                                    parsed = typeof activity.metadata === 'string' ? JSON.parse(activity.metadata) : activity.metadata;
                                                } catch (e) {
                                                    return null;
                                                }
                                                if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) return null;
                                                return (
                                                    <div style={{ 
                                                        marginTop: '0.5rem', 
                                                        padding: '0.5rem 0.75rem', 
                                                        background: 'var(--index-card-bg)', 
                                                        borderRadius: '8px', 
                                                        border: '1px solid var(--index-border-color)', 
                                                        fontSize: '0.76rem', 
                                                        display: 'flex', 
                                                        flexWrap: 'wrap', 
                                                        gap: '6px' 
                                                    }}>
                                                        {Object.entries(parsed).map(([key, value]) => (
                                                            <div key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'var(--index-hover-bg)', padding: '2px 6px', borderRadius: '4px' }}>
                                                                <strong style={{ color: 'var(--index-text-secondary)', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</strong>
                                                                <span style={{ fontFamily: 'monospace', color: 'var(--index-text-heading)' }}>
                                                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ padding: '1.1rem 1.75rem', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--index-text-secondary)', fontSize: '0.86rem', fontWeight: 600 }}>
                                                <Clock size={13} color="var(--index-text-faint)" /> 
                                                {activity.created_at ? new Date(activity.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={4} style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--index-text-faint)', fontWeight: 600 }}>
                                        No matching activity logs found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.25rem 1.75rem',
                    borderTop: '1px solid var(--index-border-subtle)',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    background: 'var(--index-hover-bg)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '0.88rem', color: 'var(--index-text-secondary)', fontWeight: 600 }}>
                            Showing <span style={{ fontWeight: 800, color: 'var(--index-text-heading)' }}>{filteredActivities.length > 0 ? startIndex + 1 : 0}</span> to <span style={{ fontWeight: 800, color: 'var(--index-text-heading)' }}>{Math.min(startIndex + itemsPerPage, filteredActivities.length)}</span> of <span style={{ fontWeight: 800, color: 'var(--index-text-heading)' }}>{filteredActivities.length}</span> records
                        </div>

                        {/* Page Size Selector */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--index-text-faint)', fontWeight: 700 }}>Per page:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--index-border-color)',
                                    background: 'var(--index-card-bg)',
                                    color: 'var(--index-text-heading)',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                }}
                            >
                                <option value={15}>15</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            style={{
                                padding: '0.45rem 0.8rem',
                                borderRadius: '8px',
                                border: '1.5px solid var(--index-border-color)',
                                background: 'var(--index-card-bg)',
                                color: currentPage === 1 ? 'var(--index-text-faint)' : 'var(--index-text-secondary)',
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Previous
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                            const isClose = Math.abs(pageNum - currentPage) <= 1;
                            const isEnd = pageNum === 1 || pageNum === totalPages;
                            if (!isClose && !isEnd) {
                                if (pageNum === 2 || pageNum === totalPages - 1) {
                                    return <span key={pageNum} style={{ padding: '0 0.4rem', color: 'var(--index-text-faint)' }}>...</span>;
                                }
                                return null;
                            }
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    style={{
                                        padding: '0.45rem 0.75rem',
                                        borderRadius: '8px',
                                        border: '1.5px solid',
                                        borderColor: currentPage === pageNum ? 'var(--index-primary-color)' : 'var(--index-border-color)',
                                        background: currentPage === pageNum ? 'var(--index-primary-color)' : 'var(--index-card-bg)',
                                        color: currentPage === pageNum ? 'white' : 'var(--index-text-secondary)',
                                        fontWeight: 800,
                                        fontSize: '0.82rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            style={{
                                padding: '0.45rem 0.8rem',
                                borderRadius: '8px',
                                border: '1.5px solid var(--index-border-color)',
                                background: 'var(--index-card-bg)',
                                color: (currentPage === totalPages || totalPages === 0) ? 'var(--index-text-faint)' : 'var(--index-text-secondary)',
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
